import { useTranslation } from 'react-i18next';

export interface CoatColorLocus {
  locus: string;
  genotype: string;
}

/** Aus den Farbgenetik-Tests abgeleitete Fellfarbe (GET /dogs/:id/health-tests/coat-color). */
export interface CoatColor {
  loci: CoatColorLocus[];
  /** "Solid", "RecessiveRed", "Sable", "Agouti", "TanPoints", "RecessiveBlack"; null = nicht bestimmbar. */
  base: string | null;
  /** "Black", "Brown", "Blue", "Isabella"; null = B oder D nicht getestet. */
  pigment: string | null;
  redIntensity: string | null;
  modifiers: string[];
  carries: string[];
  missingLoci: string[];
  recordedColor: string | null;
}

export interface CoatColorOutcome {
  base: string;
  pigment: string | null;
  modifiers: string[];
  percent: number;
}

export interface PuppyCoatColors {
  outcomes: CoatColorOutcome[];
  locusOutcomes: { locus: string; genotypes: { genotype: string; percent: number }[] }[];
  doubleMerlePercent: number | null;
}

/** Muster, bei denen rote/gelbe Haare sichtbar sind - nur dort ist die Rot-Intensitaet eine Angabe wert. */
const RED_BASES = ['RecessiveRed', 'Sable', 'Agouti', 'TanPoints'];

/** Farbwoerter je Pigmentfamilie in den vier App-Sprachen, fuer den Abgleich mit der frei eingetragenen Farbe. */
const COLOR_WORDS: Record<string, string[]> = {
  black: ['schwarz', 'black', 'noir', 'nero'],
  brown: ['braun', 'brown', 'liver', 'leber', 'chocolate', 'schoko', 'marron', 'brun', 'marrone', 'fegato', 'cioccolat'],
  blue: ['blau', 'blue', 'bleu', 'blu'],
  isabella: ['isabel', 'lilac', 'lila'],
  red: ['rot', 'red', 'gelb', 'yellow', 'fawn', 'creme', 'cream', 'crème', 'apricot', 'aprikose', 'gold', 'blond', 'rouge', 'fauve', 'jaune', 'rosso', 'fulvo', 'giallo', 'crema', 'albicocca']
};

const EXPECTED_BY_PIGMENT: Record<string, string[]> = {
  Black: ['black'],
  Brown: ['brown'],
  Blue: ['blue'],
  Isabella: ['isabella', 'brown', 'blue']
};

// Kurze Woerter nur am Wortanfang, sonst trifft "red" auch "colored".
const containsWord = (text: string, word: string) =>
  word.length <= 3 ? new RegExp(`(^|[^\\p{L}])${word}`, 'u').test(text) : text.includes(word);

/**
 * Passt die eingetragene Farbe erkennbar nicht zur DNA? Geprueft wird bewusst nur, wo die DNA eine
 * eindeutige Farbfamilie ergibt (einfarbig oder rezessiv rot) und die Eintragung ein Farbwort enthaelt -
 * Muster wie Sable oder Agouti heissen je Rasse zu unterschiedlich.
 */
export const recordedColorMismatch = (color: CoatColor) => {
  const recorded = color.recordedColor?.toLowerCase();
  if (!recorded || !color.base) return false;
  const expected =
    color.base === 'RecessiveRed'
      ? ['red']
      : (color.base === 'Solid' || color.base === 'RecessiveBlack') && color.pigment
        ? EXPECTED_BY_PIGMENT[color.pigment]
        : null;
  if (!expected) return false;
  const found = Object.entries(COLOR_WORDS)
    .filter(([, words]) => words.some((word) => containsWord(recorded, word)))
    .map(([family]) => family);
  return found.length > 0 && !found.some((family) => expected.includes(family));
};

export const useCoatColorText = () => {
  const { t } = useTranslation();

  /** "Schwarz mit Abzeichen (Tan) · Weissscheckung" - null, wenn die Grundfarbe nicht bestimmbar ist. */
  const label = (color: { base: string | null; pigment: string | null; modifiers: string[] }) => {
    if (!color.base) return null;
    const pigment = t(`health:coatColor.pigment.${color.pigment ?? 'unknown'}`);
    return [t(`health:coatColor.base.${color.base}`, { pigment }), ...color.modifiers.map((modifier) => t(`health:coatColor.modifier.${modifier}`))].join(' · ');
  };

  const carries = (codes: string[]) => codes.map((code) => t(`health:coatColor.carries.${code}`)).join(', ');

  const intensity = (color: CoatColor) =>
    color.redIntensity && color.base && RED_BASES.includes(color.base) ? t(`health:coatColor.intensity.${color.redIntensity}`) : null;

  return { label, carries, intensity };
};
